import React, { useEffect, useState } from 'react';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Users, Copy, Share2, Gift, Building2, Calendar } from 'lucide-react';
import { toast } from 'sonner';

export const Referral = () => {
  const [referralData, setReferralData] = useState(null);
  const [referredClinics, setReferredClinics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReferralData();
    fetchReferredClinics();
  }, []);

  const fetchReferralData = async () => {
    try {
      const response = await api.get('/referral/link');
      setReferralData(response.data);
    } catch (error) {
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const fetchReferredClinics = async () => {
    try {
      const response = await api.get('/referral/referred-clinics');
      setReferredClinics(response.data);
    } catch (error) {
      console.error('Failed to load referred clinics:', error);
    }
  };

  const copyToClipboard = () => {
    if (referralData?.referral_link) {
      navigator.clipboard.writeText(referralData.referral_link);
      toast.success('Referral link copied to clipboard!');
    }
  };

  const shareReferralLink = async () => {
    if (navigator.share && referralData?.referral_link) {
      try {
        await navigator.share({
          title: 'Join VetCare',
          text: 'Sign up for VetCare using my referral link!',
          url: referralData.referral_link,
        });
      } catch (error) {
        // User cancelled share or API not supported
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-gray-600">Loading referral data...</div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-12" data-testid="referral-page">
      <div className="mb-8">
        <h1 className="text-4xl font-heading font-bold text-sage-800 mb-2">Referral Program</h1>
        <p className="text-gray-600">Share VetCare with other clinics and earn referral points</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="bg-gradient-to-br from-sage-500 to-sage-600 text-white rounded-3xl p-6 shadow-xl">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-2xl">
              <Gift className="w-8 h-8" />
            </div>
            <div>
              <p className="text-sm font-medium opacity-90">Referral Points</p>
              <p className="text-4xl font-bold">{referralData?.referral_points || 0}</p>
              <p className="text-xs opacity-75 mt-1">{referredClinics.length} clinic{referredClinics.length !== 1 ? 's' : ''} referred</p>
            </div>
          </div>
        </Card>

        <Card className="bg-white rounded-3xl p-6 shadow-xl border border-stone-100 md:col-span-2">
          <h3 className="text-lg font-semibold text-sage-800 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5" />
            How It Works
          </h3>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-sage-500 font-bold">1.</span>
              <span>Share your unique referral link with other veterinary clinics</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-sage-500 font-bold">2.</span>
              <span>When they sign up using your link, you earn 1 referral point</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-sage-500 font-bold">3.</span>
              <span>Track your referrals and help grow the VetCare community!</span>
            </li>
          </ul>
        </Card>
      </div>

      <Card className="bg-white rounded-3xl p-8 shadow-xl border border-stone-100">
        <h2 className="text-2xl font-heading font-bold text-sage-800 mb-2">Your Referral Link</h2>
        <p className="text-gray-600 mb-6">Share this link to invite other clinics to join VetCare</p>
        
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <Input
            value={referralData?.referral_link || ''}
            readOnly
            className="rounded-xl bg-gray-50 text-gray-700 font-mono text-sm"
            data-testid="referral-link-input"
          />
          <Button
            onClick={copyToClipboard}
            className="rounded-xl bg-sage-500 hover:bg-sage-600 text-white px-6"
            data-testid="copy-referral-link-button"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy
          </Button>
          <Button
            onClick={shareReferralLink}
            className="rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white px-6"
            data-testid="share-referral-link-button"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
        </div>

        <div className="bg-sage-50 border border-sage-200 rounded-xl p-4">
          <p className="text-sm text-sage-700">
            <strong>Tip:</strong> Share your referral link via email, social media, or direct messaging to reach more clinics!
          </p>
        </div>
      </Card>

      {/* Referred Clinics List */}
      <Card className="bg-white rounded-3xl p-8 shadow-xl border border-stone-100 mt-6">
        <h2 className="text-2xl font-heading font-bold text-sage-800 mb-2">Referred Clinics</h2>
        <p className="text-gray-600 mb-6">Clinics that joined using your referral link</p>
        
        {referredClinics.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-2xl">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">No referrals yet</p>
            <p className="text-sm text-gray-500 mt-2">Share your referral link to start earning points!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {referredClinics.map((clinic, idx) => (
              <div
                key={clinic.id}
                className="flex items-center justify-between p-5 bg-sage-50 rounded-2xl hover:bg-sage-100 transition-colors border border-sage-100"
                data-testid={`referred-clinic-${idx}`}
              >
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-sage-500 rounded-xl">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{clinic.name}</h3>
                    <p className="text-sm text-gray-600">{clinic.address}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(clinic.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};
